import { Button, ColumnLayout } from 'AWS-UI-Components-React';
import React from 'react';
import {
  ASIN_METRIC_TYPE,
  COMPONENT_NAMES,
  HISTOGRAM_DOWNLOAD_HEADINGS,
  HISTOGRAM_METRICS,
  METRIC_NAMES,
  PER_UNIT_HISTOGRAM_DROPDOWN_ELEMENTS,
} from '../../../constants/asin_metric_elements';
import { CHANNELS } from '../../../constants/channels';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from '../../../recharts';
import { AccuracyButtonNewStyle, ASINChartStyle } from '../../../styles/common_styles';
import { downloadFile } from '../../../utilities';
import { formatTimeFrameToYYYYMM } from '../../../utils/date-utils';
import { getMaximallyDifferentRandomColor } from '../../../utils/graph-utils';
import logger from '../../../utils/logging';
import { TrendFilter } from '../../buttons/Trend_Filter';
import { EmptyMessage } from '../../content/empty_message';
import { ErrorFlash } from '../../content/error_flash';
import { LoadingSpinner } from '../../content/loading_spinner';
import { getAsinMetricsClient } from './asin_metrics_client';
import {
  extractComponent,
  extractMetric,
  filterLinesOrBars,
  generateCompositeKey,
  generateLabel,
  mergeKeysOfObjectsWithSameId,
} from './asin_metrics_utils';

export class HistogramBarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingNetVarPerUnit: false,
      loadingAbsVarPerUnit: false,
      loadError: false,
      perUnitMetric: [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT],
      perUnitComponents: ['contributionProfit', 'contributionProfitWithoutRefunds'],
      downloadingHistogram: false,
    };
    this.histogramData = {};
    this.bucketData = {};
    this.histogramMethods = {};
  };


  componentDidMount = () => {
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.asinMetricsClient = getAsinMetricsClient();
    this.histogramMethods = {
      [ASIN_METRIC_TYPE.NET_VAR_PER_UNIT]: this.asinMetricsClient.getNetVariancePerUnitHistogram,
      [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT]: this.asinMetricsClient.getAbsVariancePerUnitHistogram,
    }
    const histogramPayload = this.prepareHistogramPayload();
    HISTOGRAM_METRICS[this.props.histogramType].forEach(type => this.pullHistogramData(type, histogramPayload));
  };

  updateLoading = (type, loading) => {
    switch (type) {
      case ASIN_METRIC_TYPE.NET_VAR_PER_UNIT:
        this.setState({loadingNetVarPerUnit: loading});
        break;
      case ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT:
        this.setState({loadingAbsVarPerUnit: loading});
        break;
      default:
        logger.warn(`Attempting to update state for ${type} but was not found.`)
    }
  };

  getLoadDone = () => {
    return !this.state.loadingNetVarPerUnit && !this.state.loadingAbsVarPerUnit && !this.state.loadingSmapeVolatility;
  }

  pullHistogramData = async (type, payload) => {
    this.updateLoading(type, true);
    const method = this.histogramMethods[type];
    if(method) {
      method(this.processHistogramResults, this.onFailedRequest, payload);
    } else {
      logger.warn(`Histogram method not found for ${type}`);
    }
  };

  prepareHistogramPayload = () => {
    let channel = this.props.payload.channel;
    if(channel === CHANNELS.RETAIL) channel = 'Retail'; //Backend only accepts Retail not RETAIL
    return Object.assign({}, {
      channel, month: formatTimeFrameToYYYYMM(this.props.payload.timeFrame),
      gls: this.props.payload.gls, marketplaceId: this.props.payload.marketplace
    });
  };

  processHistogramResults = (response, type) => {
    if(response && response.data){
      this.processHistogramBuckets(response.data, type);
      this.processHistogramValues(response.data, type);
    }
    this.updateLoading(type, false);
  };

  /* Convert histogramSnapshotByComponent to a list of histogram data
   Sample Input:
   { snapshotDate: 20200107,
   { "contributionProfit":[{"bucketId":"<-50","value":100},{"bucketId":"-50to-25","value":200}, ...],
   "displayAds":[{"bucketId":"<-50","value":100},{"bucketId":"-50to-25","value":200},...]},... }
   }
   Sample Output:
   [{bucketId: "<-50", 20200107-NetLAPerUnit-Contribution Profit: 100, 20200107-NetLAPerUnit-Display Ads: 100, ...},
   {bucketId: "-50to-25", 20200107-NetLAPerUnit-Contribution Profit: 200, 20200107-NetLAPerUnit-Display Ads: 200, ...}, ...
   ]
   */
  processHistogramValues = (data, type) => {
    if(data.histogramSnapshots && data.histogramSnapshots.length && data.histogramSnapshots.length > 0){
      //TODO handle multiple gls and provide different views for NON_LOW_ASP and LOW_ASP ASINs
      const histogramSnapshotByComponent = data.histogramSnapshots[0].histogramSnapshotByComponent;
      const snapshotDate = data.histogramSnapshots[0].snapshotDate;
      const metric = type;
      /* key: bucketId, value: valueMap
       bucketId => Map(compositeKey(snapshotDate-metric-component) => value)
       */
      let bucketMap = new Map();
      let components = new Set();
      for (const componentKey of Object.keys(histogramSnapshotByComponent)){
        const buckets = histogramSnapshotByComponent[componentKey];
        const component = componentKey;
        components.add(component);
        const compositeKey = generateCompositeKey(snapshotDate, metric, component);
        for ( const bucket of buckets) {
          if (!bucketMap.has(bucket.bucketId)) {
            bucketMap.set(bucket.bucketId, new Map())
          }
          let valueMap = bucketMap.get(bucket.bucketId);
          if(!valueMap.has(compositeKey)){
            valueMap.set(compositeKey, bucket.value);
          }
        }
      }
      let histogramData = [];
      let barKeys = new Set();
      for (const [bucketId, valueMap] of bucketMap.entries()){
        let item = {};
        item[`bucketId`] = bucketId;
        for(const [key, value] of valueMap.entries()) {
          item[key] = value;
          barKeys.add(key);
        }
        histogramData.push(item);
      }
      this.histogramData[type] = {};
      this.histogramData[type].data = histogramData;
      this.histogramData[type].bars = Array.from(barKeys);
      this.histogramData[type].components = Array.from(components);
    }
  };

  processHistogramBuckets = (data, type) => {
    if(data.bucketInfoList){
      const bucketData = data.bucketInfoList;
      let buckets = {};
      bucketData.forEach(b => buckets[b.id] = b.description);
      this.bucketData[type] = buckets;
    }
  };

  getBars = (histogramType, selectedComponents, selectedMetrics) => {
    const ids = this.getHistogramBarIds(histogramType, selectedComponents, selectedMetrics);
    let bars = [];
    let colorIndex = 0;
    ids.forEach((id) => bars.push(<Bar dataKey={id}
                                       key={id}
                                       name={generateLabel(id, false, selectedMetrics && selectedMetrics.length > 1)}
                                       onClick={this.onBarClick.bind(this, id)}
                                       fill={getMaximallyDifferentRandomColor(colorIndex++, ids.length)}/>));
    return bars;
  };

  onBarClick = (barId, data) => {
    const bucketId = data.payload.bucketId;
    const component = extractComponent(barId);
    const metric = extractMetric(barId);
    this.props.pullAsinInfo(metric, bucketId, component);
  };

  /*
   Filter histogram bars based on selected components and metrics
   */
  getHistogramBarIds = (histogramType, selectedComponents, selectedMetrics) => {
    const metrics = HISTOGRAM_METRICS[histogramType];
    let combinedBars = metrics.filter(type => this.histogramData && this.histogramData[type] && this.histogramData[type].bars)
                              .map(type => this.histogramData[type].bars)
                              .flat();
    combinedBars = filterLinesOrBars(combinedBars, selectedComponents, selectedMetrics);
    return combinedBars;
  };

  getComponentDropDownElements = (histogramType) => {
    const metrics = HISTOGRAM_METRICS[histogramType];
    let combinedComponents = metrics.filter(type => this.histogramData && this.histogramData[type] && this.histogramData[type].components)
                                    .map(type => this.histogramData[type].components);
    let uniqueComponents = [...new Set(combinedComponents.flat())];
    return uniqueComponents.map(comp => {return {id: comp, label: COMPONENT_NAMES[comp]}; });
  };

  setPerUnitMetric = (chosen) => {
    const ids = chosen.detail.selectedIds;
    this.setState({perUnitMetric: ids})
  };

  setPerUnitHistogramComponents = (chosen) => {
    const ids = chosen.detail.selectedIds;
    this.setState({perUnitComponents: ids});
  };

  onFailedRequest = () => {
    this.setState({loadError: true});
  };

  getHistogramData = (histogramType) => {
    const metrics = HISTOGRAM_METRICS[histogramType];
    let combinedData = metrics.filter(type => this.histogramData && this.histogramData[type] && this.histogramData[type].data)
                              .map(type => this.histogramData[type].data)
                              .flat();
    /*
     Merge keys of histogram rows with the same bucketId
     Sample Input:
     [{bucketId: "<-50", 20200107-NetLAPerUnit-Contribution Profit: 100, 20200107-NetLAPerUnit-Display Ads: 100},
     {bucketId: "<-50", 20200107-NetFAPerUnit-Contribution Profit: 100, 20200107-NetFAPerUnit-Display Ads: 100}]
     Sample Output:
     [{bucketId: "<-50", 20200107-NetLAPerUnit-Contribution Profit: 100, 20200107-NetLAPerUnit-Display Ads: 100,
     20200107-NetFAPerUnit-Contribution Profit: 100, 20200107-NetFAPerUnit-Display Ads: 100}]
     */
    return mergeKeysOfObjectsWithSameId(combinedData, 'bucketId');
  };

  onDownloadData = (histogramName, histogramType) => {
    this.setState({downloadingHistogram: true})
    const metrics = HISTOGRAM_METRICS[histogramType];
    const histogramData = this.histogramData || {};
    let combinedData = !histogramData ? [] : metrics.filter(type => histogramData[type] && histogramData[type].data)
                                                    .map(type => histogramData[type].data)
                                                    .flat();
    const {channel, marketplace, productLines, timeFrame} = this.props.payload;
    let downloadDataArr = [HISTOGRAM_DOWNLOAD_HEADINGS];
    combinedData.forEach(row => {
      const bucketId = row.bucketId;
      for(const key of Object.keys(row)){
        if(key !== 'bucketId'){
          const metric = extractMetric(key);
          const metricName = METRIC_NAMES[metric];
          const component = COMPONENT_NAMES[extractComponent(key)];
          const asinCount = row[key];
          const bucket = this.bucketData[metric] && this.bucketData[metric][bucketId];
          downloadDataArr.push([marketplace, channel, timeFrame, productLines, component, metricName, bucket, asinCount]);
        }
      }
    });
    const downloadData = downloadDataArr.map(rowArr => rowArr.join('\t')).join('\n');
    downloadFile(downloadData, `${histogramName} Data.xls`);
    this.setState({downloadingHistogram: false});
  };

  formatXAxis = (tickItem) => {
    return tickItem.substring(0, 1);
  }

  render() {
    const {histogramName, histogramType} = this.props;
    const selectedMetrics = this.state.perUnitMetric;
    const componentOpts = this.getComponentDropDownElements(histogramType);
    const selectedComponents = this.state.perUnitComponents;
    const histogramData = this.getHistogramData(histogramType);
    const histogramDataEmpty = !histogramData || !histogramData.length;

    return (
      <div className={ASINChartStyle}>
        <div className="awsui-util-container" >
          <div className="awsui-util-container-header">
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <div className="awsui-util-font-size-2">
                  {histogramName}
                </div>
              </div>
              <Button
                text="Download"
                variant="normal"
                disabled={!this.getLoadDone() || histogramDataEmpty}
                loading={this.state.downloadingHistogram}
                onClick={this.onDownloadData.bind(this, histogramName, histogramType)}
                data-cy="report-download-button"
              />
            </div>
          </div>
          {this.getLoadDone() && !histogramDataEmpty &&
          <div>
            <ColumnLayout columns={2}>
              <div data-awsui-column-layout-root="true">
                <div>
                  <TrendFilter
                    text="Component"
                    classname={AccuracyButtonNewStyle}
                    options={componentOpts}
                    onChange={this.setPerUnitHistogramComponents}
                    selectedIds={selectedComponents}
                  />
                </div>
                <div>
                  <TrendFilter
                    text="ASIN Metric"
                    classname='AccuracyTypeMultiSelect'
                    options={PER_UNIT_HISTOGRAM_DROPDOWN_ELEMENTS}
                    onChange={this.setPerUnitMetric}
                    selectedIds={selectedMetrics}
                  />
                </div>
              </div>
            </ColumnLayout>
            <div style={{width:'99%', padding:'0'}}>
              <ResponsiveContainer aspect={2.5}>
                <BarChart
                  data={ histogramData }
                  margin={ {top: 10, right: 30, left: 20, bottom: 10,} }
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucketId" tickFormatter={this.formatXAxis}
                         label={{value: 'Per Unit Variance Buckets', position: 'insideBottomLeft', dx: -10, dy: 10}}
                  />
                  <YAxis label={{value: 'ASIN Count (Millions)', angle: -90, position: 'insideLeft', dy: 30}}
                         tickFormatter={(val)=> val/1000000}/>
                  <Tooltip />
                  <Legend margin={{ top: 0, left: 0, right: 0, bottom: 0 } }/>
                  {this.getBars(histogramType, selectedComponents, selectedMetrics)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          }
          {!this.getLoadDone() && !this.state.loadError && <LoadingSpinner />}
          {this.getLoadDone() && histogramDataEmpty && <EmptyMessage />}
          {this.state.loadError && (<ErrorFlash type={histogramName} />)}
        </div>
      </div>);
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/histogram_bar_chart.jsx